import { useNavigate } from 'react-router-dom';
import { Button, Table, Space } from 'antd';
import { ChevronLeft } from 'react-feather';
import { PlusOutlined, EditOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';

import styles from './stylesWithdrawalReviewRules.module.css';
import { SIDER_TABS } from '../../../../utils/Constants';
import { NAVIGATION_ROUTES } from '../../../../utils/NavigationUtils';
import Layout from '../../../CommonComponents/Layout/Layout';
import {
  useWithdrawalReview,
  WithdrawalReviewProvider,
} from '../WithdrawalReviewContext';

const periodConstants = {
  SINGLE_TRANSACTION: 'Single Transaction',
  HOUR: 'Hour',
  DAY: 'Day',
  WEEK: 'Week',
  MONTH: 'Month',
  YEAR: 'Year',
  ALL_TIME: 'All Time',
};

function WithdrawalReviewRulesContent() {
  const navigate = useNavigate();

  const { withdrawalRules, loadingRules } = useWithdrawalReview();

  const handleAddRule = () => {
    navigate(NAVIGATION_ROUTES.WITHDRAWAL_REVIEW_ADD_RULE);
  };

  const handleEditRule = (rule) => {
    navigate(NAVIGATION_ROUTES.WITHDRAWAL_REVIEW_EDIT_RULE, {
      state: { rule },
    });
  };

  const getPeriodLabel = (value) => {
    return periodConstants[value] || value;
  };

  const columns = [
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      sorter: (a, b) => a.name.localeCompare(b.name),
      render: (text) => <span className={styles.tableText}>{text}</span>,
    },
    {
      title: 'Min Trx Amount',
      dataIndex: 'min_transaction_amount',
      key: 'min_transaction_amount',
      sorter: (a, b) => {
        const aAmount = parseFloat(a.min_transaction_amount);
        const bAmount = parseFloat(b.min_transaction_amount);
        return aAmount - bAmount;
      },
      render: (text) => <span className={styles.tableText}>${text}</span>,
    },
    {
      title: 'Max Trx Amount',
      dataIndex: 'max_transaction_amount',
      key: 'max_transaction_amount',
      sorter: (a, b) => {
        const aAmount = parseFloat(a.max_transaction_amount);
        const bAmount = parseFloat(b.max_transaction_amount);
        return aAmount - bAmount;
      },
      render: (text) => <span className={styles.tableText}>${text}</span>,
    },
    {
      title: 'Number of Trx',
      dataIndex: 'num_transactions',
      key: 'num_transactions',
      sorter: (a, b) => a.num_transactions - b.num_transactions,
      render: (text) => <span className={styles.tableText}>{text}</span>,
    },
    {
      title: 'Period',
      dataIndex: 'period_type',
      key: 'period_type',
      sorter: (a, b) => a.period_type.localeCompare(b.period_type),
      render: (text) => (
        <span className={styles.tableText}>{getPeriodLabel(text)}</span>
      ),
    },
    {
      title: 'Created',
      dataIndex: 'created',
      key: 'created',
      sorter: (a, b) => dayjs(a.created).unix() - dayjs(b.created).unix(),
      render: (text) => (
        <span className={styles.tableText}>
          {dayjs(text).format('MMM DD, YYYY HH:mm')}
        </span>
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Space size="middle">
          <Button
            type="text"
            icon={<EditOutlined />}
            onClick={() => handleEditRule(record)}
            className={styles.actionButton}
          />
        </Space>
      ),
    },
  ];

  return (
    <Layout name={SIDER_TABS.TRANSACTIONS}>
      <div className={styles.container}>
        <div className={styles.header}>
          <div className={styles.headerLeft}>
            <button
              className={styles.backButton}
              onClick={() => navigate(NAVIGATION_ROUTES.WITHDRAWAL_REVIEW)}
            >
              <ChevronLeft size={20} />
            </button>
            <h2 className={styles.title}>Withdrawal Review Rules</h2>
          </div>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={handleAddRule}
            className={styles.addButton}
          >
            Add New Rule
          </Button>
        </div>

        <div className={styles.tableContainer}>
          <Table
            columns={columns}
            dataSource={withdrawalRules?.rules}
            rowKey="rule_id"
            loading={loadingRules}
            pagination={false}
            className={styles.table}
          />
        </div>
      </div>
    </Layout>
  );
}

export default function WithdrawalReviewRules() {
  return (
    <WithdrawalReviewProvider>
      <WithdrawalReviewRulesContent />
    </WithdrawalReviewProvider>
  );
}
